import { ListFilter } from "lucide-react";
import type { AgentSummary } from "../../types";
import { ColorDot } from "../status-badge";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

/** Picks which stations' runs the production history shows. Every station is shown until it is unticked. */
export function StationFilter({
  stations,
  hidden,
  agentById,
  onChange,
}: {
  stations: ReadonlyArray<{ agentId: string; runs: number }>;
  hidden: ReadonlySet<string>;
  agentById: Map<string, AgentSummary>;
  onChange: (hidden: string[]) => void;
}) {
  const shown = stations.filter((s) => !hidden.has(s.agentId)).length;
  const filtered = shown < stations.length;
  const toggle = (agentId: string, visible: boolean) => {
    const next = new Set(hidden);
    if (visible) next.delete(agentId);
    else next.add(agentId);
    onChange(stations.map((s) => s.agentId).filter((id) => next.has(id)));
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 text-xs" aria-label="Filter by station">
          <ListFilter />
          Stations
          {filtered && (
            <span className="text-muted-foreground tabular-nums">
              {shown}/{stations.length}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-52">
        <DropdownMenuLabel>Show runs from</DropdownMenuLabel>
        {stations.map(({ agentId, runs }) => {
          const agent = agentById.get(agentId);
          return (
            <DropdownMenuCheckboxItem
              key={agentId}
              checked={!hidden.has(agentId)}
              onCheckedChange={(checked) => toggle(agentId, checked)}
              onSelect={(e) => e.preventDefault()}
            >
              {agent && <ColorDot color={agent.station.color} />}
              <span className="flex-1">{agent?.name ?? agentId}</span>
              <span className="text-xs text-muted-foreground tabular-nums">{runs}</span>
            </DropdownMenuCheckboxItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled={!filtered} onSelect={() => onChange([])}>
          Show all stations
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
